import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useLabelContext } from '@/design_system/Label/Label';
import { createBEMClasses } from '@/utils/classname';

import './RadioCard.css';

const { block, element } = createBEMClasses('radio__card');

interface RadioCardGroupContext {
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  direction?: 'row' | 'column';
  allowDeselect?: boolean;
}

const RadioCardGroupContext = createContext<RadioCardGroupContext>({
  name: '',
  value: null,
  onChange: () => {},
});

export function RadioCard<T extends string>({
  value,
  ariaLabel,
  disabled,
  isInvalid,
  picture,
  backgroundColor = '#F7F8F7',
  children,
}: {
  value: T;
  ariaLabel: string;
  disabled?: boolean;
  isInvalid?: boolean;
  picture?: ReactNode;
  backgroundColor?: string;
  children: ReactNode;
}) {
  const {
    name,
    value: groupValue,
    onChange,
    disabled: groupDisabled,
    isInvalid: groupIsInvalid,
    direction,
    allowDeselect,
  } = useContext(RadioCardGroupContext);

  const isChecked = value === groupValue;
  const isDisabled = disabled || groupDisabled;
  const invalid = isInvalid || groupIsInvalid;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={block({
          checked: isChecked,
          disabled: isDisabled,
          invalid,
          direction,
        })}
        onClick={() => {
          if (!isDisabled) {
            onChange(allowDeselect && isChecked ? null : value);
          }
        }}
      >
        <input
          type="radio"
          name={name}
          value={value}
          checked={isChecked}
          aria-label={ariaLabel}
          onChange={() => onChange(value)}
          disabled={isDisabled}
        />
        <div className={element('content')}>
          {picture && (
            <div className={element('picture')} style={{ backgroundColor }}>
              {picture}
            </div>
          )}
          <div className={element('children')}>{children}</div>
        </div>
      </div>
    </>
  );
}

export interface RadioCardGroupProps<T extends string> {
  name: string;
  ariaLabel?: string;
  value?: T | null;
  onChange?: (value: T | null) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  direction?: 'row' | 'column';
  theme?: 'default' | 'brand';
  children?: ReactNode;
  scrollToOnMount?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
  style?: React.CSSProperties;
  allowDeselect?: boolean;
}

export function RadioCardGroup<T extends string>({
  name,
  ariaLabel,
  value = null,
  onChange = () => {},
  disabled,
  isInvalid,
  direction = 'row',
  theme = 'default',
  children,
  scrollToOnMount,
  ref,
  style,
  allowDeselect = false,
}: RadioCardGroupProps<T>) {
  /**
   * Radio inputs cannot have a single html label for all radio inputs.
   * Therefore, we don't use the `id` here, and use the label as aria-label to be selectable by Playwright.
   */
  const { label } = useLabelContext();

  const internalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref) {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      internalRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ref]);

  return (
    <div
      ref={scrollToOnMount ? (ref ?? internalRef) : null}
      className={element('group', { direction, theme })}
      aria-label={ariaLabel ?? label}
      style={style}
    >
      <RadioCardGroupContext
        value={{
          name,
          value,
          onChange: onChange as (value: string | null) => void,
          disabled,
          isInvalid,
          direction,
          allowDeselect,
        }}
      >
        {children}
      </RadioCardGroupContext>
    </div>
  );
}

export const RadioCardYesNo = (props: Omit<RadioCardGroupProps<'yes' | 'no'>, 'children'>) => {
  const { t } = useLingui();

  return (
    <RadioCardGroup {...props}>
      <RadioCard value="yes" ariaLabel={t({ id: 'design-system.radio.yes', message: 'Yes' })}>
        <Trans id="design-system.radio.yes">Yes</Trans>
      </RadioCard>
      <RadioCard value="no" ariaLabel={t({ id: 'design-system.radio.no', message: 'No' })}>
        <Trans id="design-system.radio.no">No</Trans>
      </RadioCard>
    </RadioCardGroup>
  );
};
